import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ConvertibleDeal {
  id: string;
  name: string;
  total_amount: number;
  currency: string;
  stage: string;
  created_at: string;
  contact_name?: string;
}

export interface ConfirmedPayment {
  id: string;
  amount: number;
  due_date: string;
  paid_at: string | null;
  currency: string;
}

export interface UpsellConversionParams {
  originDealId: string;
  destinationDealId: string;
  originalDealValue: number;
  creditToTransfer: number;
}

export interface UpsellCalculation {
  originalDealValue: number;
  confirmedPaymentsTotal: number;
  newDealValue: number;
  remainingBalance: number;
  newSaleValue: number;
}

export function useUpsellConversion(contactId?: string) {
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch deals from the same contact that can be converted
  const { data: convertibleDeals, isLoading: dealsLoading } = useQuery({
    queryKey: ['convertible-deals', contactId],
    queryFn: async () => {
      if (!contactId) return [];

      const { data, error } = await supabase
        .from('deals')
        .select(`
          id,
          name,
          total_amount,
          currency,
          stage,
          created_at,
          contacts:contact_id (
            first_name,
            last_name
          )
        `)
        .eq('contact_id', contactId)
        .in('stage', ['won', 'proposal', 'negotiation', 'contact', 'scheduled'])
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map((deal: any) => ({
        id: deal.id,
        name: deal.name,
        total_amount: deal.total_amount || 0,
        currency: deal.currency || 'USD',
        stage: deal.stage,
        created_at: deal.created_at,
        contact_name: deal.contacts 
          ? `${deal.contacts.first_name || ''} ${deal.contacts.last_name || ''}`.trim()
          : undefined,
      })) as ConvertibleDeal[];
    },
    enabled: !!contactId,
  });

  // Fetch confirmed payments for a specific deal
  const fetchConfirmedPayments = async (dealId: string): Promise<ConfirmedPayment[]> => {
    const { data, error } = await supabase
      .from('payments')
      .select('id, amount, due_date, paid_at, currency')
      .eq('deal_id', dealId)
      .eq('status', 'paid')
      .order('due_date', { ascending: true });

    if (error) throw error;

    return (data || []).map((p) => ({
      id: p.id,
      amount: p.amount || 0,
      due_date: p.due_date,
      paid_at: p.paid_at,
      currency: p.currency || 'USD',
    }));
  };

  // Calculate upsell values
  const calculateUpsellValues = (
    originDeal: ConvertibleDeal,
    destinationDealValue: number,
    confirmedPayments: ConfirmedPayment[]
  ): UpsellCalculation => {
    const originalDealValue = originDeal.total_amount;
    const confirmedPaymentsTotal = confirmedPayments.reduce((sum, p) => sum + p.amount, 0);
    const newDealValue = destinationDealValue;
    const remainingBalance = newDealValue - confirmedPaymentsTotal;
    const newSaleValue = newDealValue - originalDealValue;

    return {
      originalDealValue,
      confirmedPaymentsTotal,
      newDealValue,
      remainingBalance,
      newSaleValue,
    };
  };

  // Process the upsell conversion
  const processConversion = useMutation({
    mutationFn: async (params: UpsellConversionParams) => {
      setIsProcessing(true);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Get app_user id
      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!appUser) throw new Error('App user não encontrado');

      // Call the RPC function
      const { data, error } = await supabase.rpc('process_upsell_conversion', {
        p_origin_deal_id: params.originDealId,
        p_destination_deal_id: params.destinationDealId,
        p_original_deal_value: params.originalDealValue,
        p_credit_to_transfer: params.creditToTransfer,
        p_user_id: appUser.id,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; payments_transferred?: number };
      
      if (!result.success) {
        throw new Error(result.error || 'Erro ao processar conversão');
      }

      return result;
    },
    onSuccess: (data) => {
      toast.success('Conversão de Upsell realizada com sucesso!', {
        description: `${data.payments_transferred || 0} pagamentos transferidos`,
      });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['convertible-deals'] });
      queryClient.invalidateQueries({ queryKey: ['seller-performance'] });
    },
    onError: (error: Error) => {
      toast.error('Erro ao processar conversão', {
        description: error.message,
      });
    },
    onSettled: () => {
      setIsProcessing(false);
    },
  });

  return {
    convertibleDeals,
    dealsLoading,
    fetchConfirmedPayments,
    calculateUpsellValues,
    processConversion: processConversion.mutate,
    isProcessing: isProcessing || processConversion.isPending,
  };
}
